import { memo, useEffect, useState } from 'react';
import type { Employee } from '@/types/employee';
import { Button } from '@/components/ui/Button';

const remainingLabel = (availableAt: string, now: number) => {
  const seconds = Math.max(0, Math.ceil((new Date(availableAt).getTime() - now) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

type EmployeeRowProps = {
  employee: Employee;
  sending: boolean;
  sessionsVerified: boolean;
  onSend: (employee: Employee) => void;
};

export const EmployeeRow = memo(
  ({ employee, sending, sessionsVerified, onSend }: EmployeeRowProps) => {
    const [now, setNow] = useState(Date.now());
    const availableAt = employee.smsAvailableAt;
    const coolingDown = Boolean(availableAt && new Date(availableAt).getTime() > now);

    useEffect(() => {
      setNow(Date.now());
      if (!availableAt || new Date(availableAt).getTime() <= Date.now()) return;
      const timer = window.setInterval(() => {
        const next = Date.now();
        setNow(next);
        if (next >= new Date(availableAt).getTime()) window.clearInterval(timer);
      }, 1000);
      return () => window.clearInterval(timer);
    }, [availableAt]);

    const disabled =
      sending ||
      coolingDown ||
      !sessionsVerified ||
      !employee.hasActiveSession ||
      !employee.phoneMasked ||
      !employee.hasTsdCredentials;
    const title = !sessionsVerified
      ? 'Не удалось проверить открытую сессию'
      : !employee.hasActiveSession
        ? 'Нет открытой сессии'
        : !employee.hasTsdCredentials
          ? 'Данные входа на ТСД не загружены'
          : undefined;

    return (
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 font-medium text-gray-900">{employee.fullName || '—'}</td>
        <td className="px-4 py-3">{employee.num || '—'}</td>
        <td className="px-4 py-3">{employee.position || '—'}</td>
        <td className="px-4 py-3">{employee.storeName || `Магазин ${employee.storeId}`}</td>
        <td className="px-4 py-3">{employee.phoneMasked || 'Не указан'}</td>
        <td className="px-4 py-3 text-right">
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={() => onSend(employee)}
            title={title}
            className="min-w-[230px] disabled:bg-gray-300"
          >
            {sending
              ? 'Отправка…'
              : coolingDown && availableAt
                ? `Повторно через ${remainingLabel(availableAt, now)}`
                : 'Данные для входа на ТСД'}
          </Button>
        </td>
      </tr>
    );
  },
);

EmployeeRow.displayName = 'EmployeeRow';
